(() => {
  if (customElements.get('dps-product-guard')) return;

  class DPSProductGuard extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.info = this.closest('product-info');
      if (!this.form || !this.info) return;
      try {
        this.config = JSON.parse(this.querySelector('[data-guard-config]').textContent);
      } catch (_) {
        return;
      }
      this.variant = this.config.variant;
      this.confirmed = new Map();
      this.sizeIndexes = this.config.options.reduce((indexes, name, index) => {
        if (/size/i.test(name)) indexes.push(index);
        return indexes;
      }, []);
      if (this.config.explicitVariant && !this.hasPlaceholder(this.config.explicitVariant)) {
        this.sizeIndexes.forEach((index) => this.confirmed.set(index, this.config.explicitVariant.options[index]));
      }
      this.pending = false;
      this.requestFailed = false;
      this.onOptionChange = this.handleOptionChange.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onFormInput = this.syncAcceleratedCheckout.bind(this);
      this.info.addEventListener('change', this.onOptionChange, true);
      this.info.addEventListener('input', this.onFormInput);
      this.info.addEventListener('change', this.onFormInput);
      this.form.addEventListener('variant:change', this.onVariantChange);
      this.form.addEventListener('submit', this.onSubmit, true);
      document.addEventListener('click', this.onClick, true);
      this.sync();
    }

    disconnectedCallback() {
      clearTimeout(this.pendingTimer);
      this.info?.removeEventListener('change', this.onOptionChange, true);
      this.info?.removeEventListener('input', this.onFormInput);
      this.info?.removeEventListener('change', this.onFormInput);
      this.form?.removeEventListener('variant:change', this.onVariantChange);
      this.form?.removeEventListener('submit', this.onSubmit, true);
      document.removeEventListener('click', this.onClick, true);
    }

    get picker() {
      return document.getElementById(this.dataset.pickerId) || this.info?.querySelector('variant-picker');
    }

    isPlaceholder(value) {
      if (!value) return true;
      const prompts = new Set(['select', 'choose', 'selectsize', 'selectasize', 'selectyoursize', 'pleaseselectsize', 'choosesize', 'chooseasize', 'chooseyoursize', 'selectoption', 'selectanoption', 'chooseoption', 'chooseanoption', 'selectcolor', 'choosecolor', 'selectcolour', 'choosecolour', 'selectwidth', 'choosewidth', 'pleaseselect', 'pleaseselectasize', 'pleasechoose', 'pleasechoosesize']);
      return prompts.has(value.toLowerCase().replace(/[^a-z0-9]/g, ''));
    }

    hasPlaceholder(variant) {
      return !variant || variant.options.some((value) => this.isPlaceholder(value));
    }

    selectionMatches() {
      if (!this.variant || this.hasPlaceholder(this.variant)) return false;
      if (!this.sizeIndexes.every((index) => this.confirmed.get(index) === this.variant.options[index])) return false;
      const picker = this.picker;
      if (!picker) return this.sizeIndexes.length === 0;
      return [...picker.querySelectorAll('fieldset, .product-form__input--dropdown')].every((group, index) => {
        const input = group.querySelector('input[type="radio"]:checked, select');
        return input && input.value === this.variant.options[index];
      });
    }

    valid() {
      return !this.pending && this.selectionMatches() && this.variant.available;
    }

    availabilityRequestReady() {
      return !this.pending && this.selectionMatches();
    }

    acceleratedCheckoutReady() {
      if (!this.valid()) return false;
      if (this.info?.querySelector('input[name="bundles"]:checked')) return false;
      return [...(this.form.elements || [])].every((control) => {
        if (control.disabled) return true;
        if (control.type === 'file' && control.files?.length) return false;
        return !control.willValidate || control.validity.valid;
      });
    }

    syncAcceleratedCheckout() {
      const ready = this.acceleratedCheckoutReady();
      this.form.querySelectorAll('[data-dps-accelerated-checkout]').forEach((checkout) => {
        checkout.hidden = !ready;
        checkout.toggleAttribute('inert', !ready);
      });
    }

    showPendingState() {
      clearTimeout(this.pendingTimer);
      this.pending = true;
      this.requestFailed = false;
      this.syncAcceleratedCheckout();
      this.form.setAttribute('aria-busy', 'true');
      const status = this.querySelector('.dps-selection-status');
      if (status) {
        status.hidden = false;
        status.textContent = this.dataset.loading;
      }
      const reload = this.querySelector('.dps-selection-reload');
      if (reload) reload.hidden = true;
      this.pendingTimer = setTimeout(() => {
        if (!this.isConnected || !this.pending) return;
        this.requestFailed = true;
        this.form.setAttribute('aria-busy', 'false');
        if (status) status.textContent = this.dataset.loadError;
        if (reload) reload.hidden = false;
      }, 12000);
    }

    handleOptionChange(event) {
      const picker = event.target.closest('variant-picker');
      if (!picker || picker !== this.picker || !event.target.matches('input[type="radio"], select')) return;
      const group = event.target.closest('[data-dps-size-index]');
      if (group) {
        const index = Number(group.dataset.dpsSizeIndex) - 1;
        if (this.isPlaceholder(event.target.value)) this.confirmed.delete(index);
        else this.confirmed.set(index, event.target.value);
      }
      this.showPendingState();
      const input = this.form.querySelector('input[name="id"]');
      if (input) input.value = '';
    }

    handleVariantChange(event) {
      clearTimeout(this.pendingTimer);
      this.variant = event.detail.variant;
      this.pending = false;
      this.requestFailed = false;
      this.form.setAttribute('aria-busy', 'false');
      const reload = this.querySelector('.dps-selection-reload');
      if (reload) reload.hidden = true;
      this.sync();
    }

    sync() {
      const picker = this.picker;
      if (picker) {
        if (this.sizeIndexes.length) picker.removeAttribute('data-dynamic-variants');
        picker.querySelectorAll('[data-dps-size-index]').forEach((group) => {
          const index = Number(group.dataset.dpsSizeIndex) - 1;
          const confirmed = this.confirmed.get(index);
          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            input.checked = !!confirmed && input.value === confirmed && !this.isPlaceholder(input.value);
          });
          const select = group.querySelector('select');
          if (select) {
            select.value = confirmed || '';
            [...select.options].forEach((option) => option.toggleAttribute('selected', option.selected));
          }
          const valueLabel = group.querySelector('.form__label span[id]');
          if (valueLabel) valueLabel.textContent = confirmed || this.dataset.prompt;
        });
      }
      const matches = this.selectionMatches();
      const valid = this.valid();
      const input = this.form.querySelector('input[name="id"]');
      if (input) input.value = valid ? this.variant.id : '';
      this.form.querySelectorAll('[id^="BuyButtonPrice-"]').forEach((price) => { price.hidden = !matches; });
      const status = this.querySelector('.dps-selection-status');
      const needsSize = this.sizeIndexes.some((index) => !this.confirmed.has(index));
      if (status) {
        status.hidden = valid;
        status.textContent = !this.config.productAvailable && needsSize ? this.dataset.notifyChoice : !this.config.productAvailable ? this.dataset.soldout : needsSize || (this.variant && this.hasPlaceholder(this.variant)) ? this.dataset.error : this.variant?.available === false ? this.dataset.soldout : this.dataset.unavailable;
      }
      const buttons = new Set([...this.form.querySelectorAll('[type="submit"][name="add"]'), ...document.querySelectorAll(`[type="submit"][name="add"][form="${CSS.escape(this.form.id)}"]`)]);
      buttons.forEach((button) => {
        const label = !this.config.productAvailable ? this.dataset.soldout : needsSize || (this.variant && this.hasPlaceholder(this.variant)) ? this.dataset.prompt : matches && this.variant?.available ? (this.dataset.preorder === 'true' ? this.dataset.preorderLabel : this.dataset.cartLabel) : this.variant?.available === false ? this.dataset.soldout : this.dataset.unavailable;
        const text = button.querySelector('.btn-text > span:not(.relative)');
        if (text) text.textContent = label;
        button.setAttribute('aria-label', label);
        if (needsSize && this.config.productAvailable) button.disabled = false;
      });
      this.syncAcceleratedCheckout();
    }

    showError(forAvailability = false) {
      const status = this.querySelector('.dps-selection-status');
      if (status) {
        status.hidden = false;
        const needsSize = this.sizeIndexes.some((index) => !this.confirmed.has(index));
        status.textContent = this.requestFailed ? this.dataset.loadError : this.pending ? this.dataset.loading : needsSize || (this.variant && this.hasPlaceholder(this.variant)) ? (forAvailability ? this.dataset.notifyChoice : this.dataset.error) : this.variant?.available === false ? this.dataset.soldout : this.dataset.unavailable;
      }
      const missing = this.sizeIndexes.find((index) => !this.confirmed.has(index));
      const group = this.picker?.querySelector(`[data-dps-size-index="${(missing ?? this.sizeIndexes[0] ?? 0) + 1}"]`) || this.picker;
      const control = group?.querySelector('input[type="radio"]:not([disabled]), select');
      if (group) group.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
      (control || status)?.focus({ preventScroll: true });
    }

    handleSubmit(event) {
      if (this.valid()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError();
    }

    handleClick(event) {
      const accelerated = event.target.closest('[data-dps-accelerated-checkout]');
      if (accelerated && accelerated.closest('form') === this.form && !this.acceleratedCheckoutReady()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.syncAcceleratedCheckout();
        if (!this.valid()) this.showError();
        else this.form.reportValidity();
        return;
      }
      const notification = event.target.closest('button.product-form__alert');
      if (notification && notification.closest('form') === this.form) {
        if (this.availabilityRequestReady()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError(true);
        return;
      }
      const button = event.target.closest('button[type="submit"][name="add"]');
      if (!button || button.form !== this.form || this.valid()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError();
    }
  }
  customElements.define('dps-product-guard', DPSProductGuard);

})();
